<template>
  <div class="floating-chat-window" :class="`role-${userRole}`">
    <div class="chat-header flex justify-content-between align-items-center p-3 bg-primary text-white border-round-top-lg">
      <span class="font-bold">Your Chats</span>
      <Button 
        icon="pi pi-times" 
        class="p-button-rounded p-button-text p-button-sm close-btn" 
        @click="$emit('close')" 
      />
    </div>
    
    <div class="chat-body overflow-y-auto">
      <div v-if="loading" class="p-4 text-center">
        <i class="pi pi-spin pi-spinner text-2xl"></i>
      </div>
      
      <div v-else-if="channels.length === 0" class="p-4 text-center text-gray-500">
        No active conversations.
      </div>

      <div v-else class="flex flex-column gap-2 p-2">
        <div 
          v-for="channel in channels" 
          :key="channel.cid" 
          class="channel-item flex align-items-center gap-3 p-2 cursor-pointer border-round"
          @click="$emit('select-channel', channel)"
        >
          <img 
            :src="channel.data.image || blankProfile" 
            class="w-3rem h-3rem border-circle" 
            alt="chat"
          />
          
          <div class="flex flex-column flex-1">
            <span class="font-semibold text-sm text-900">
              {{ getDisplayName(channel) }}
            </span>
            <span class="text-xs text-gray-500 text-overflow-ellipsis w-10rem white-space-nowrap overflow-hidden">
              {{ getLastMessage(channel) }}
            </span>
          </div>

          <span 
            v-if="channel.countUnread() > 0"
            class="ml-auto bg-orange-500 text-white border-circle w-2rem h-2rem flex align-items-center justify-content-center text-xs font-bold"
          >
            {{ channel.countUnread() > 99 ? '99+' : channel.countUnread() }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineEmits, computed } from 'vue';
import Button from 'primevue/button';
import { chatClient } from '@/stores/authStore';
import { useAuthStore } from '@/stores/authStore';
import blankProfile from '@/assets/blank-profile.png';

defineEmits(['close', 'select-channel']);

const channels = ref([]);
const loading = ref(true);
const authStore = useAuthStore();
const userRole = computed(() => authStore.user?.role || 'student');
const currentUserId = chatClient.userID;

const getDisplayName = (channel) => {
  try{
  if (channel.data.name && !channel.data.name.startsWith('Chat with')) {
    return channel.data.name;
  }
  
  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user.id !== currentUserId
  );
  
  return otherMember ? otherMember.user.name : 'Unknown User';
} catch (error) {
  console.log(error);
  return 'Unknown User';
}
};

const fetchChannels = async () => {
  loading.value = true;
  try {
    const queryResult = await chatClient.queryChannels(
      { 
        type: 'messaging', 
        members: { $in: [chatClient.userID] } 
      },
      { last_message_at: -1 },
      { 
        limit: 20, 
        state: true,
        watch: true
      }
    );
    channels.value = queryResult;
  } catch (error) {
    console.error("Error fetching channels:", error);
  } finally {
    loading.value = false;
  }
};

const getLastMessage = (channel) => {
  const messages = channel.state.messages;
  if (messages.length === 0) return 'No messages yet';
  return messages[messages.length - 1].text || 'Attachment';
};

const handleNewMessage = () => {
    fetchChannels();
};

onMounted(() => {
  fetchChannels();
  try{
  chatClient.on('message.new', handleNewMessage);
  } catch (error) {
    console.log(error);
  }
});

onUnmounted(() => {
  try{
  chatClient.off('message.new', handleNewMessage);
  } catch (error) {
    console.log(error);
  }
});
</script>

<style scoped>
.floating-chat-window {
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 380px;
  height: min(580px, calc(100vh - 120px));
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  border: 1px solid var(--surface-border);
}

/* Bottom-anchored layouts only — from 768px up the panel docks to the top instead. */
@media (max-width: 767px) and (max-height: 700px) {
  .floating-chat-window {
    bottom: 20px;
    height: calc(100vh - 40px);
  }
}

@media (max-width: 600px) {
  .floating-chat-window {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

/* Position only — height stays on the base rule above. */
@media (min-width: 768px) {
  .floating-chat-window {
    top: calc(10.5vh + 15px);
    bottom: auto;
    right: 40px;
  }
}

.chat-header {
  height: 60px;
  background-color: var(--primary-color);
}

.role-student .chat-header {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%) !important;
}

.role-tutor .chat-header {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%) !important;
}

.chat-body {
  flex: 1;
  background-color: white;
  overflow-y: auto;
}

.channel-item {
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--surface-border);
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
}

.channel-item:hover {
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.close-btn {
  color: white !important;
}

.role-student .close-btn:hover {
  color: var(--orange-500) !important;
}

.role-tutor .close-btn:hover {
  color: var(--teal-500) !important;
}
</style>